import type { CanvasView } from './canvas-types';

type Navigation = {
  activeProjectId: number | null;
  activeTaskId: number | null;
  folderProjectId: number | null;
  standaloneActiveView: CanvasView;
  activeBrandId: number | null;
  activeAdAccountId: number | null;
};

type ReferenceState = Navigation & {
  projects: Record<number, { loadedAt?: number | null; loading?: boolean } | undefined>;
  switchProject: (id: number, meta: { brandId: number | null; adAccountId: number | null; parentId: number | null }) => Promise<void>;
  addChatAttachment: (attachment: { url: string; name: string }) => void;
  setActiveView: (view: 'chat-only') => void;
};

type Dependencies = {
  getState: () => ReferenceState;
  restoreActive: (navigation: Navigation) => void;
  request: typeof fetch;
};

const navigationOf = (state: ReferenceState): Navigation => ({
  activeProjectId: state.activeProjectId,
  activeTaskId: state.activeTaskId,
  folderProjectId: state.folderProjectId,
  standaloneActiveView: state.standaloneActiveView,
  activeBrandId: state.activeBrandId,
  activeAdAccountId: state.activeAdAccountId,
});

const sameDestination = (state: ReferenceState, previous: Navigation) =>
  state.activeProjectId === previous.activeProjectId &&
  state.activeTaskId === previous.activeTaskId &&
  state.folderProjectId === previous.folderProjectId &&
  state.standaloneActiveView === previous.standaloneActiveView;

export async function attachReferenceToExistingTask(deps: Dependencies, projectId: number, imageUrl: string, imageName: string): Promise<string> {
  if (!imageUrl) throw new Error('This ad has no image to use as a reference.');
  const previous = navigationOf(deps.getState());
  const response = await deps.request(`/api/canvas/projects/${projectId}`);
  if (!response.ok) throw new Error('Could not open this task. Please choose another task or try again.');
  const project = await response.json();
  if (!sameDestination(deps.getState(), previous)) throw new Error('The active task changed. Choose the reference destination again.');
  if (project.id !== projectId || project.isFolder || project.is_folder) throw new Error('Choose a task to use this reference.');

  const store = deps.getState();
  try {
    if (store.activeProjectId !== projectId || store.projects[projectId]?.loadedAt == null) await store.switchProject(projectId, {
      brandId: project.brandId ?? project.brand_id ?? null,
      adAccountId: project.adAccountId ?? project.ad_account_id ?? null,
      parentId: project.parentId ?? project.parent_id ?? null,
    });
    const loaded = deps.getState();
    if (loaded.activeProjectId !== projectId || loaded.projects[projectId]?.loadedAt == null || loaded.projects[projectId]?.loading) {
      throw new Error('Could not load this task. Please try again.');
    }
    loaded.addChatAttachment({ url: imageUrl, name: imageName });
    loaded.setActiveView('chat-only');
  } catch (error) {
    // Only undo our own failed switch. A later user navigation always wins.
    if (previous.activeProjectId !== projectId && deps.getState().activeProjectId === projectId) deps.restoreActive(previous);
    throw error;
  }
  return project.name || 'the selected task';
}
